import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">分类不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: category.name },
          ]}
        />

        {/* Category Header */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-primary-600 text-2xl`}></i>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
                {category.name}
              </h1>
              <p className="text-foreground-500 text-sm max-w-lg mx-auto">
                {category.description}
              </p>
              <span className="inline-block mt-3 text-xs text-foreground-400">
                共 {category.count.toLocaleString()} 条内容
              </span>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              {categoryArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-foreground-300 text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer overflow-hidden"
                    >
                      <div className="h-48 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-[11px] font-medium">
                            {article.category}
                          </span>
                          {article.isHot && (
                            <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[11px] font-medium flex items-center gap-0.5">
                              <i className="ri-fire-line text-[10px]"></i>热
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-[11px] text-foreground-400">
                          <span>{article.publishDate}</span>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-chat-3-line"></i>
                              {article.comments}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Other Categories */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">
                探索更多分类
              </h3>
              <div className="flex flex-wrap gap-3">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="px-5 py-2.5 rounded-full bg-background-50 border border-background-200/70 text-sm text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                      {cat.name}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 吃瓜群`,
            description: category.description,
            url: `https://www.chiguaqun.com/category/${category.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '吃瓜群',
              url: 'https://www.chiguaqun.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.chiguaqun.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category.name,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}